import { FC, useEffect, useState } from 'react';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { ShippingDetailsQuotesTableRow } from './ShippingDetailsQuotesTableRow';
import { ShippingDetailsRequestQuote } from '../ShippingDetailsText/ShippingDetailsRequestQuote';
import { ShippingDetailsPromo } from '../ShippingDetailsPromo/ShippingDetailsPromo';
import { shippingModal as messages } from '../shippingMessages';
import { Spinner } from 'dibs-elements/exports/Spinner';
import { trackEvent } from 'dibs-tracking';

import styles from './ShippingDetailsQuotes.scss';

import { ShippingDetailsQuotesTable_viewer$key } from './__generated__/ShippingDetailsQuotesTable_viewer.graphql';
import { ShippingDetailsQuotesTable_item$key } from './__generated__/ShippingDetailsQuotesTable_item.graphql';

type Props = {
    viewer: ShippingDetailsQuotesTable_viewer$key;
    item: ShippingDetailsQuotesTable_item$key;
    currency: string;
    country: string;
    zipCode: string;
    quoteRequestStatus: string;
    openQuoteRequestModal: () => void;
    blockRequest?: boolean;
    isLoading?: boolean;
    isOrderInUS?: boolean;
};

const ShippingDetailsQuotesTable: FC<Props> = ({
    currency,
    viewer: viewerRef,
    item: itemRef,
    country,
    zipCode,
    quoteRequestStatus,
    openQuoteRequestModal,
    blockRequest,
    isLoading,
}) => {
    const [isEventSent, setIsEventSent] = useState(false);
    const item = useFragment(
        graphql`
            fragment ShippingDetailsQuotesTable_item on Item {
                ...ShippingDetailsQuotesTableRow_item
                ...ShippingDetailsPromo_item
                serviceId
            }
        `,
        itemRef
    );

    const viewer = useFragment(
        graphql`
            fragment ShippingDetailsQuotesTable_viewer on Viewer {
                ...ShippingDetailsQuotesTableRow_viewer
                itemDeliveryOptions: availableDeliveryOptions(
                    first: 100
                    itemId: $itemId
                    country: $country
                    zipCode: $zipCode
                    addressResolutionType: $addressResolutionType
                    isCheckout: false
                    quantity: $itemQuantity
                    userId: $userId
                ) @include(if: $shouldFetchItemDelivery) {
                    edges {
                        node {
                            ...ShippingDetailsQuotesTableRow_shipmentQuote
                            sourceId
                            serviceId
                            isLocalPickup
                            showQuoteAsPending
                            isCustom
                            totalAmount {
                                amount
                            }
                            serviceMethod {
                                serviceMethodType
                                serviceType
                                windowMinDays
                                windowMaxDays
                            }
                        }
                    }
                }
                transactionDeliveryOptions: availableDeliveryOptions(
                    first: 100
                    transactionId: $transactionId
                    country: $country
                    zipCode: $zipCode
                    addressResolutionType: $addressResolutionType
                    isCheckout: false
                    quantity: $itemQuantity
                    userId: $userId
                ) @include(if: $shouldFetchTransactionDelivery) {
                    edges {
                        node {
                            ...ShippingDetailsQuotesTableRow_shipmentQuote
                            sourceId
                            serviceId
                            isLocalPickup
                            showQuoteAsPending
                            isCustom
                            totalAmount {
                                amount
                            }
                            serviceMethod {
                                serviceMethodType
                                serviceType
                                windowMinDays
                                windowMaxDays
                            }
                        }
                    }
                }
            }
        `,
        viewerRef
    );

    const { transactionDeliveryOptions, itemDeliveryOptions } = viewer || {};
    const transactionOptionsEdges = transactionDeliveryOptions?.edges || [];
    const itemOptionsEdges = itemDeliveryOptions?.edges || [];

    const availableOptions = transactionOptionsEdges
        .concat(
            itemOptionsEdges.filter(itemOption => {
                const {
                    serviceId,
                    totalAmount: itemOptionTotalAmount,
                    serviceMethod: itemOptionServiceMethod,
                } = itemOption?.node || {};
                const {
                    serviceMethodType: itemOptionServiceMethodType,
                    serviceType: itemOptionServiceType,
                    windowMinDays: itemOptionWindowMinDays,
                    windowMaxDays: itemOptionWindowMaxDays,
                } = itemOptionServiceMethod || {};

                return !transactionOptionsEdges.some(transactionOption => {
                    const {
                        sourceId,
                        totalAmount: transactionOptionTotalAmount,
                        serviceMethod: transactionOptionServiceMethod,
                    } = transactionOption?.node || {};
                    const {
                        serviceMethodType: transactionOptionServiceMethodType,
                        serviceType: transactionOptionServiceType,
                        windowMinDays: transactionOptionWindowMinDays,
                        windowMaxDays: transactionOptionWindowMaxDays,
                    } = transactionOptionServiceMethod || {};
                    return (
                        String(serviceId) === String(sourceId) ||
                        (itemOptionTotalAmount?.amount === transactionOptionTotalAmount?.amount &&
                            itemOptionServiceMethodType === transactionOptionServiceMethodType &&
                            itemOptionServiceType === transactionOptionServiceType &&
                            itemOptionWindowMinDays === transactionOptionWindowMinDays &&
                            itemOptionWindowMaxDays === transactionOptionWindowMaxDays)
                    );
                });
            })
        )
        .map(option => option?.node || null)
        .filter(filterNulls)
        .sort((q1, q2) => (q1?.totalAmount?.amount || 0) - (q2?.totalAmount?.amount || 0));

    const preQuotes = (availableOptions || []).filter(
        q => !q.isLocalPickup && !q?.showQuoteAsPending
    );
    const localPickupQuote = (availableOptions || []).find(q => q.isLocalPickup);

    useEffect(() => {
        if (!isEventSent && (itemDeliveryOptions || transactionDeliveryOptions)) {
            trackEvent(
                {
                    category: 'shipping options',
                    action: 'shipping options modal displayed',
                    label: `${country} | ${zipCode}`,
                    value: (preQuotes || []).length,
                },
                null
            );
            setIsEventSent(true);
        }
    }, [country, zipCode, preQuotes, isEventSent, itemDeliveryOptions, transactionDeliveryOptions]);

    return (
        <div className={styles.quotesTableWrapper}>
            {!preQuotes.length && !isLoading && (
                <ShippingDetailsRequestQuote
                    quoteRequestStatus={quoteRequestStatus}
                    openQuoteRequestModal={openQuoteRequestModal}
                    blockRequest={blockRequest}
                    isShippingOptionsDisclaimer
                />
            )}
            <table className={styles.quotesTable}>
                <thead>
                    <tr>
                        <th
                            data-tn="ship-method-value"
                            className={classnames(styles.quotesTableHeaderCell, styles.methodCell)}
                        >
                            {messages.method}
                        </th>
                        <th
                            data-tn="ship-time-value"
                            className={classnames(
                                styles.quotesTableHeaderCell,
                                styles.timePeriodCell
                            )}
                        >
                            {messages.time}
                        </th>
                        <th
                            data-tn="ship-price-value"
                            className={classnames(styles.priceData, styles.quotesTableHeaderCell)}
                        >
                            {messages.price}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {isLoading ? (
                        <tr>
                            <td colSpan={3}>
                                <Spinner black containerClass={styles.spinnerWrapper} />
                            </td>
                        </tr>
                    ) : (
                        <>
                            {!!preQuotes.length &&
                                preQuotes.map(preQuote => (
                                    <ShippingDetailsQuotesTableRow
                                        key={preQuote?.serviceId}
                                        currency={currency}
                                        shipmentQuote={preQuote}
                                        viewer={viewer}
                                        item={item}
                                    />
                                ))}
                            {localPickupQuote && (
                                <ShippingDetailsQuotesTableRow
                                    key="local-pickup-option"
                                    currency={currency}
                                    shipmentQuote={localPickupQuote}
                                    viewer={viewer}
                                    item={item}
                                />
                            )}
                            <ShippingDetailsQuotesTableRow
                                key="buyer-arranged-option"
                                currency={currency}
                                shipmentQuote={null}
                                viewer={viewer}
                                item={item}
                                isBuyerArrangedRow
                            />
                        </>
                    )}
                </tbody>
            </table>
            <ShippingDetailsPromo item={item} />
        </div>
    );
};

export default ShippingDetailsQuotesTable;
